// src/components/ImageProcessor.js
import React, { useState, useRef, useEffect } from 'react';
import ProcessedImageBox from './utilities/ProcessedImageBox';
import InputImageBox from './utilities/InputImageBox';

// Shared color list
const COLOR_NAMES = [
  'Pink',
  'Beige',
  'Red',
  'Deep Red',
  'Brown',
  'Light Brown',
  'Yellow',
  'Orange',
  'Olive Green',
  'Green',
  'Pine Green',
  'Turquoise',
  'Blue',
  'Deep Blue',
  'Light Blue',
  'Purple',
  'White',
  'Black',
  'Grey',
];

 export default function ImageProcessor({
   filename,
   setFilename,
   isProcessed,
   setIsProcessed,
   email,
   setErrorState,
   errorState,
   credits,
   processedImage,
   setProcessedImage,
   overlayImage,
   setOverlayImage,
   setColorListExternal,
   resetTrigger,
   onResetComplete,
   inputImageUrl,
   initialBaseColor,
   initialColorList,
   initAdvancedPanelKey,
   onStartNewProject,
   capitanApi,
   hydrateGate = true,
   projectRoot,
   initialRemoveBkgrnd = false,
   initialCloseBkrndHoles = false,
   initialRemoveIslands = false,
   initialSpeckClean = false,
   initialMkFreestanding = false,
   initialBckgrndInflate = 40,
 }) {

  const invalidateBasicControls = () => {
    setDropdownDefaultsVisible(true);
    setProcessedImage(null);
    setOverlayImage(null);
    setProcessDisabled(false);
    setIsProcessed(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);
  };

  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [inputImage, setInputImage] = useState(null);
  const [numColors, setNumColors] = useState(4);
  const [pendingNumColors, setPendingNumColors] = useState(4);
  const [backgroundInflate, setBackgroundInflate] = useState(50);
  const [inflationValues, setInflationValues] = useState([]);

  const [colorList, setColorList] = useState([]);
  const [dropdownDefaultsVisible, setDropdownDefaultsVisible] = useState(true);
  const [baseColor, setBaseColor] = useState('');
  const [processDisabled, setProcessDisabled] = useState(true);

  const [speckleFlags, setSpeckleFlags] = useState([]);
  const [removeNoisyBackground, setRemoveNoisyBackground] = useState(false);
  const [closeBackgroundHoles, setCloseBackgroundHoles] = useState(true);
  const [thresholdValues, setThresholdValues] = useState([]);
  const [showColorControls, setShowColorControls] = useState(false);
  const [removeSmallIslands, setRemoveSmallIslands] = useState(false);
  const [speckleAutoCleanup, setSpeckleAutoCleanup] = useState(true);
  const [makeFreestanding, setMakeFreestanding] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processedLoaded, setProcessedLoaded] = useState(false);
  const [overlayLoaded, setOverlayLoaded] = useState(false);

  const [showAdvancedOverlayPanel, setShowAdvancedOverlayPanel] = useState(false);

  const orangeBoxRef = useRef(null);

  // --- One-shot auto-open flags (no auto-close) ------------------------------
  const autoOpenAdvancedOnceRef = useRef(false);

  const [aiModeEnabled, setAiModeEnabled] = useState(false);
  const [aiAllowed, setAiAllowed] = useState(false);

  const [uploadSessionToken, setUploadSessionToken] = useState(0);

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const res = await fetch('/ai-public/', { cache: 'no-store' });
      if (!res.ok) {
        if (!cancelled) setAiModeEnabled(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!cancelled) setAiModeEnabled(Boolean(data?.ai_mode));
    } catch {
      if (!cancelled) setAiModeEnabled(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);


useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      if (!email) {
        if (!cancelled) setAiAllowed(false);
        return;
      }

      const res = await fetch('/ai-allowed/', {
        method: 'GET',
        headers: { user: email },
        cache: 'no-store',
      });

      if (!res.ok) {
        if (!cancelled) setAiAllowed(false);
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!cancelled) setAiAllowed(Boolean(data?.allowed));
    } catch {
      if (!cancelled) setAiAllowed(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [email]);


  // --- URL helpers ------------------------------------------------------------
const toPreviewPrefix = React.useCallback((url) => {
  if (!url) return '';
  const s = String(url).trim();
  if (s.startsWith('/preview/')) return s;
  if (s.startsWith('/outputs/')) return '/preview/' + s.slice('/outputs/'.length);
  return s;
  }, []);

const normalizeUrl = React.useCallback(
  (p) => {
    if (!p) return '';
    let url = String(p).trim();

    if (/^(https?:)?\/\//i.test(url) || /^data:/i.test(url) || url.startsWith('blob:')) {
      try {
        const u = new URL(url, window.location.origin);
        u.pathname = toPreviewPrefix(u.pathname);
        return u.toString();
      } catch {
        return toPreviewPrefix(url);
      }
    }

    if (!url.startsWith('/')) url = '/' + url;
    url = toPreviewPrefix(url);
    return url;
  },
  [toPreviewPrefix]
);



const extractProjectRoot = (urlLike) => {
  if (!urlLike) return '';
  const s = String(urlLike);
  const m = s.match(/(\/(?:outputs|preview)\/[^/]+\/output-[^/]+)/i);
  return m ? m[1] : '';
};

  const buildRootFromEmailFilename = (emailArg, filenameArg) => {
    const username = (emailArg || '').split('@')[0];
    const baseNoExt = String(filenameArg || '').split('.').slice(0, -1).join('');
    if (!username || !baseNoExt) return '';
    return `/preview/${username}/output-${baseNoExt}`;
  };

  const userIntentRef = useRef('idle'); // 'idle' | 'upload' | 'open'

  const paddedInflate = [
    backgroundInflate,
    ...Array(pendingNumColors - 1)
      .fill(0)
      .map((_, i) => Number(inflationValues[i + 1] ?? 0)),
  ];
  const paddedSpeckles = [...Array(pendingNumColors)].map((_, i) =>
    Number(speckleFlags[i] ?? 0)
  );
  const paddedThresholds = [...Array(pendingNumColors)].map((_, i) =>
    Number(thresholdValues[i] ?? 0)
  );

  const colorUpdateTimer = useRef(null);
  const lastSentColorsRef = useRef(null);
  const canSendColorUpdates = () =>
    isProcessed && !dropdownDefaultsVisible && !isProcessing;

  const scheduleColorUpdate = (nextSelectedColors) => {
    if (!canSendColorUpdates()) return;
    const key = JSON.stringify(nextSelectedColors);
    if (lastSentColorsRef.current === key) return;
    if (colorUpdateTimer.current) clearTimeout(colorUpdateTimer.current);
    colorUpdateTimer.current = setTimeout(() => {
      lastSentColorsRef.current = key;
      triggerColorUpdate(nextSelectedColors);
    }, 350);
  };

  const triggerColorUpdate = async (selectedColors) => {
    try {
      setIsProcessing(true);
      const res = await fetch('/update-colors/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: selectedColors.length,
          selectedColors,
        }),
      });
      if (!res.ok) {
        console.error('update-colors failed:', await res.text());
        setErrorState(true);
        setIsProcessing(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      updatePreviews(data);
      setErrorState(false);
    } catch (e) {
      console.error('update-colors error:', e);
      setErrorState(true);
      setIsProcessing(false);
    }
  };

  // ----------------- HARD RESET before new upload -----------------
  function resetAllLocalStateForNewUpload() {
    setMakeFreestanding(false);
    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
      colorUpdateTimer.current = null;
    }
    lastSentColorsRef.current = null;
    setUploadSessionToken(0);
    setInputImage(null);
    setProcessedImage(null);
    setOverlayImage(null);
    setIsProcessed(false);
    setProcessDisabled(false);
    setErrorState(false);

    setBaseColor('');
    setColorList([]);
    setNumColors(4);
    setPendingNumColors(4);
    setDropdownDefaultsVisible(true);
    setShowColorControls(false);
    setSpeckleFlags([0, 0, 0, 0, 0, 0, 0, 0]);
    setThresholdValues(Array(8).fill(0));
    setInflationValues([]);
    setBackgroundInflate(50);
    setRemoveNoisyBackground(false);
    setCloseBackgroundHoles(true);
    setRemoveSmallIslands(false);
    setSpeckleAutoCleanup(true);
    setMakeFreestanding(false);
    setProgress(null);

    setIsProcessing(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);

    setShowAdvancedOverlayPanel(false);

    autoOpenAdvancedOnceRef.current = false;

    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ----------------- OPEN PROJECT hydrate -----------------
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;

    userIntentRef.current = 'open';
    setShowColorControls(true);

    if (!inputImageUrl && !filename && !initAdvancedPanelKey) return;

    if (inputImageUrl) setInputImage(inputImageUrl);
    setProcessDisabled(false);
    setIsProcessed(true);

    const derivedRoot =
      projectRoot ||
      extractProjectRoot(inputImageUrl || '') ||
      buildRootFromEmailFilename(email, filename);

    if (!derivedRoot) {
      console.warn(
        '[ImageProcessor] Could not determine project root. Pass projectRoot from App.js.'
      );
    }

    const ts = Date.now();
    if (derivedRoot) {
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      setOverlayImage(
        `${normalizeUrl(`${derivedRoot}/vectors/combined_colors.png`)}?t=${ts}`
      );
    }
    setDropdownDefaultsVisible(false);

    (async () => {
      if (!derivedRoot) return;
      const metaUrl = `${normalizeUrl(
        `${derivedRoot}/metadata.json`
      )}?ts=${Date.now()}`;
      try {
        const res = await fetch(metaUrl, { cache: 'no-store' });
        if (!res.ok) {
          console.warn(
            '[ImageProcessor] metadata fetch failed:',
            res.status,
            res.statusText
          );
          return;
        }
        const meta = await res.json();

        let baseRel = String(meta?.base_svg || '').replace(/^\.\//, '').trim();
        if (baseRel) {
          if (!baseRel.includes('/')) baseRel = `vectors/base/${baseRel}`;
          setProcessedLoaded(false);
          setProcessedImage(
            `${normalizeUrl(`${derivedRoot}/${baseRel}`)}?t=${Date.now()}`
          );
        } else {
          console.warn(
            '[ImageProcessor] metadata.base_svg missing; overlay will be visible alone.'
          );
        }

        const normPath = (p) =>
          String(p || '')
            .replace(/^\.\//, '')
            .replace(/\/+/g, '/')
            .trim()
            .toLowerCase();

        const pickColorName = (p) => {
          if (!p || typeof p !== 'string') return '';
          const mm = p.match(
            /(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i
          );
          return mm && mm[1] ? mm[1].trim() : '';
        };

        const snap = (c) => {
          if (!c) return '';
          const found = COLOR_NAMES.find(
            (n) => n.toLowerCase() === String(c).toLowerCase()
          );
          return found || c;
        };

        const baseSvgRel = String(meta?.base_svg || '')
          .replace(/^\.\//, '')
          .trim();
        const baseSvgNorm = normPath(baseSvgRel);
        let base = pickColorName(baseSvgRel);

        let rest = Array.isArray(meta.colors)
          ? meta.colors
              .filter((e) => e && (e.file || e.name))
              .filter((e) => String(e.name).toLowerCase() !== 'base')
              .filter((e) => {
                const fileNorm = normPath(e.file);
                return !(fileNorm && baseSvgNorm && fileNorm === baseSvgNorm);
              })
              .map((e) =>
                e.file ? pickColorName(e.file) : String(e.name || '')
              )
              .filter(Boolean)
          : [];

        if (!base && Array.isArray(meta.colors)) {
          const baseObj = meta.colors.find(
            (c) => String(c?.name).toLowerCase() === 'base'
          );
          if (baseObj?.file) base = pickColorName(baseObj.file);
          if (!base && baseObj?.name) base = String(baseObj.name).trim();
        }

        const baseNorm = snap(base);
        const restNorm = rest.map(snap).filter(Boolean);
        const parsed = [baseNorm, ...restNorm].filter(Boolean);

        if (parsed.length >= 2) {
          const total = Math.min(8, Math.max(2, parsed.length));
          setBaseColor(parsed[0]);
          setColorList(parsed.slice(1));
          setNumColors(total);
          setPendingNumColors(total);
          setDropdownDefaultsVisible(false);
          setIsProcessed(true);
        }
      } catch (err) {
        console.error('[ImageProcessor] metadata load error:', err);
        requestAnimationFrame(() => setDropdownDefaultsVisible(false));
      }
    })();
  }, [inputImageUrl, initAdvancedPanelKey, filename, projectRoot]); // eslint-disable-line

  // ---------- Hydrate processing settings ----------
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;

    const toBool = (v, fb = false) => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v !== 0;
      if (typeof v === 'string') {
        const t = v.trim().toLowerCase();
        if (['true', '1', 'yes', 'y', 'on'].includes(t)) return true;
        if (['false', '0', 'no', 'n', 'off'].includes(t)) return false;
      }
      return fb;
    };
    const clampInt = (n, lo, hi, fb) => {
      const v = parseInt(n, 10);
      if (!Number.isFinite(v)) return fb;
      return Math.max(lo, Math.min(hi, v));
    };

    setRemoveNoisyBackground(toBool(initialRemoveBkgrnd, false));
    setCloseBackgroundHoles(toBool(initialCloseBkrndHoles, false));
    setRemoveSmallIslands(toBool(initialRemoveIslands, false));
    setSpeckleAutoCleanup(toBool(initialSpeckClean, false));
    setMakeFreestanding(toBool(initialMkFreestanding, false));

    const safeInflate = clampInt(initialBckgrndInflate, 10, 100, 40);
    setBackgroundInflate(safeInflate);
  }, [
    hydrateGate,
    initAdvancedPanelKey,
    projectRoot,
    initialRemoveBkgrnd,
    initialCloseBkrndHoles,
    initialRemoveIslands,
    initialSpeckClean,
    initialMkFreestanding,
    initialBckgrndInflate,
  ]);

  // Reset on external reset
  useEffect(() => {
    if (resetTrigger) {
      autoOpenAdvancedOnceRef.current = false;
      setUploadSessionToken(0);
      setInputImage(null);
      setProcessedImage(null);
      setOverlayImage(null);
      setFilename('');
      setIsProcessed(false);
      setErrorState(false);
      setColorListExternal([]);
      setBaseColor('');
      setColorList([]);
      setPendingNumColors(4);
      setNumColors(4);
      setDropdownDefaultsVisible(true);
      setShowColorControls(false);
      setIsProcessing(false);
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      setShowAdvancedOverlayPanel(false);
      onResetComplete?.();
      if (fileInputRef.current) fileInputRef.current.value = '';
      userIntentRef.current = 'idle';
    }
  }, [
    resetTrigger,
    onResetComplete,
    setProcessedImage,
    setOverlayImage,
    setFilename,
    setIsProcessed,
    setErrorState,
    setColorListExternal,
  ]);

  // ---------- Robust preview updater ----------
  const updatePreviews = (data) => {
    const ts = Date.now();
    const d = data || {};

    const preview =
      d.preview_image ||
      d.previewImage ||
      d.processed_image ||
      d.processedImage ||
      d.base_svg ||
      d.baseSvg ||
      null;

    const overlay =
      d.overlay_image ||
      d.overlayImage ||
      d.combined_colors ||
      d.combinedColors ||
      d.flat_image ||
      d.flatImage ||
      null;

    if (preview) {
      setProcessedLoaded(false);
      setProcessedImage(`${normalizeUrl(preview)}?t=${ts}`);
    } else if (processedImage) {
      setProcessedLoaded(false);
      setProcessedImage(
        `${normalizeUrl(processedImage.split('?')[0])}?t=${ts}`
      );
    }

    if (overlay) {
      setOverlayLoaded(false);
      setOverlayImage(`${normalizeUrl(overlay)}?t=${ts}`);
    } else if (overlayImage) {
      setOverlayLoaded(false);
      setOverlayImage(
        `${normalizeUrl(overlayImage.split('?')[0])}?t=${ts}`
      );
    }
  };

  // sync internal -> external color list
  // NOTE: we avoid pushing an empty array on initial mount;
  // true resets are handled explicitly in the reset effect.
  useEffect(() => {
    if (typeof setColorListExternal !== 'function') return;

    const hasAnyColor =
      (typeof baseColor === 'string' && baseColor.trim().length > 0) ||
      (Array.isArray(colorList) && colorList.length > 0);

    // Don’t overwrite parent with [] on first render / pre-hydrate.
    if (!hasAnyColor) return;

    const combined = baseColor
      ? [baseColor, ...(colorList || [])]
      : [...(colorList || [])];

    setColorListExternal(combined);
  }, [baseColor, colorList, setColorListExternal]);


  const handleInputClick = () => {
    capitanApi?.onUploadStart?.();
    userIntentRef.current = 'upload';
    resetAllLocalStateForNewUpload();
    onStartNewProject?.();
    fileInputRef.current?.click();
  };

  // Size arrays when color count changes
  useEffect(() => {
    setThresholdValues(Array(pendingNumColors).fill(0));
    setSpeckleFlags([0, ...Array(pendingNumColors - 1).fill(0)]);
  }, [pendingNumColors]);

  // Progress polling
  useEffect(() => {
    const username = (email || '').split('@')[0];
    if (!username) return;
    const interval = setInterval(() => {
      fetch(`/progress/${username}?ts=${Date.now()}`)
        .then((res) => {
          if (!res.ok) throw new Error('Progress not ready');
          return res.json();
        })
        .then((data) => {
          if (typeof data.progress === 'number') {
            setProgress(data.progress);
            setErrorState(false);
          }
        })
        .catch(() => setProgress(null));
    }, 1500);
    return () => clearInterval(interval);
  }, [email, setErrorState]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Only JPG, JPEG, or PNG files are allowed.');
      return;
    }

    const formData = new FormData();
    const username = (email || '').split('@')[0];
    formData.append('file', file);
    formData.append('username', username);

    // UI reset for new upload — keep Process disabled for now
    setInputImage(URL.createObjectURL(file));
    setIsProcessed(false);
    setDropdownDefaultsVisible(true);
    setBaseColor('');
    setColorList([]);
    setProcessDisabled(true);      // ⬅⬅⬅ stay disabled until upload SUCCESS
    setProcessedImage(null);
    setOverlayImage(null);
    setIsProcessing(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);

    try {
      const response = await fetch('/upload/', {
        method: 'POST',
        body: formData,
      });

    if (response.ok) {
      const data = await response.json();
      setFilename(data.filename);
      setUploadSessionToken((x) => x + 1);
      setErrorState(false);

      // Fire AFTER we have a real filename (upload completed)
      capitanApi?.onImageUploadedToInputBox?.({
        filename: data.filename,
        email,
      });

      setProcessDisabled(false);
    }
else {
        console.error('Upload failed:', await response.text());
        setErrorState(true);
        setProcessDisabled(true);  // ⬅ keep disabled on failure
      }
    } catch (error) {
      console.error('Error during upload:', error);
      setErrorState(true);
      setProcessDisabled(true);    // ⬅ keep disabled on failure
    }

    orangeBoxRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleAICleanupDone = async (result) => {
    if (result?.ok) {
      await handleProcessClick(false);
    }
  };
  const handleProcessClick = async (collectInputs = false) => {
    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
      colorUpdateTimer.current = null;
    }
    lastSentColorsRef.current = null;

    if (!collectInputs) {
      setBaseColor('');
      setColorList([]);
      setDropdownDefaultsVisible(true);
      setShowColorControls(false);
    }

    let selectedColors = [];
    let inflateValuesLocal = [];
    if (collectInputs) {
      selectedColors = [
        (baseColor || '').trim(),
        ...Array.from({ length: pendingNumColors - 1 }, (_, i) =>
          (colorList[i] || '').trim()
        ),
      ].slice(0, pendingNumColors);
      inflateValuesLocal = [
        backgroundInflate,
        ...inflationValues.slice(1, pendingNumColors),
      ];
    }

    setNumColors(pendingNumColors);
    if (!collectInputs) setInflationValues(Array(pendingNumColors).fill(0));

    autoOpenAdvancedOnceRef.current = false; // allow auto-open for this new processing run

    setIsProcessing(true);
    setProcessDisabled(true);

    setProcessedImage(null);
    setOverlayImage(null);
    setProcessedLoaded(false);
    setOverlayLoaded(false);
    setIsProcessed(false);

    try {
      const response = await fetch('/process/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: pendingNumColors,
          backgroundInflate,
          inflateValues: collectInputs ? inflateValuesLocal : [],
          selectedColors: collectInputs ? selectedColors : [],
          speckleFlags: collectInputs ? speckleFlags : [],
          thresholdValues: Array.from(
            { length: pendingNumColors },
            (_, i) => thresholdValues[i] || 0
          ),
          removeNoisyBackground,
          closeBackgroundHoles,
          removeSmallIslands,
          speckleAutoCleanup,
          makeFreestanding,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        if (
          data.adjusted_num_colors &&
          data.adjusted_num_colors !== pendingNumColors
        ) {
          setNumColors(data.adjusted_num_colors);
          setPendingNumColors(data.adjusted_num_colors);
        }

        updatePreviews(data);
        setDropdownDefaultsVisible(false);
        setErrorState(false);
        setIsProcessed(true);

        // --- UPDATED COLOR SYNC (process) -----------------------------------
      if (Array.isArray(data.color_names) && data.color_names.length > 0) {
        const rawBase = String(data.color_names[0] || '').trim();
        const baseResolved =
          COLOR_NAMES.find(c => c.toLowerCase() === rawBase.toLowerCase()) ||
          rawBase;

        const userColors = data.color_names.slice(1).map(c => {
          const name = String(c || '').trim();
          return (
            COLOR_NAMES.find(n => n.toLowerCase() === name.toLowerCase()) || name
          );
        });

        setBaseColor(baseResolved);
        setColorList(userColors);

        // ⬅⬅⬅ again, the FIX
        if (typeof setColorListExternal === 'function') {
          setColorListExternal([baseResolved, ...userColors]);
        }
      } else {
        setBaseColor('');
        setColorList([]);
        if (typeof setColorListExternal === 'function') {
          setColorListExternal([]);
        }
      }

      } else {
        console.error('Processing failed:', await response.text());
        setErrorState(true);
      }
    } catch (error) {
      console.error('Error during processing:', error);
      setErrorState(true);
    } finally {
      setProcessDisabled(false);
    }

    orangeBoxRef.current?.scrollTo({
      top: orangeBoxRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  // ---- Reusable Update Design ----
  const handleUpdateDesign = async (overrides = {}) => {
    const {
      num = numColors,
      base = baseColor,
      colors = colorList,
      background = backgroundInflate,
      inflate = paddedInflate,
      speckles = paddedSpeckles,
      thresholds = paddedThresholds,
      rnb = removeNoisyBackground,
      cbh = closeBackgroundHoles,
      rsi = removeSmallIslands,
      sac = speckleAutoCleanup,
      mfs = makeFreestanding,
    } = overrides;

    setIsProcessing(true);
    window.dispatchEvent(new CustomEvent('resetSTLDownloads'));

    setProcessedLoaded(false);
    setOverlayLoaded(false);

    try {
      const selectedColorsLocal = [
        (base || '').trim(),
        ...Array.from(
          { length: Math.max(0, (num || 0) - 1) },
          (_, i) => (colors[i] || '').trim()
        ),
      ].slice(0, num || 0);

      const response = await fetch('/update-design/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: num,
          backgroundInflate: background,
          inflateValues: inflate,
          selectedColors: selectedColorsLocal,
          speckleFlags: speckles,
          thresholdValues: thresholds,
          removeNoisyBackground: rnb,
          closeBackgroundHoles: cbh,
          removeSmallIslands: rsi,
          speckleAutoCleanup: sac,
          makeFreestanding: mfs,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        updatePreviews(data);
        setErrorState(false);

        // --- UPDATED COLOR SYNC (update-design) -----------------------------
      if (Array.isArray(data.color_names) && data.color_names.length > 0) {
        const rawBase = String(data.color_names[0] || '').trim();
        const baseResolved =
          COLOR_NAMES.find(c => c.toLowerCase() === rawBase.toLowerCase()) ||
          rawBase;

        const userColors = data.color_names.slice(1).map(c => {
          const name = String(c || '').trim();
          return (
            COLOR_NAMES.find(n => n.toLowerCase() === name.toLowerCase()) || name
          );
        });

        setBaseColor(baseResolved);
        setColorList(userColors);

        // ⬅⬅⬅ THE FIX — immediately sync to parent
        if (typeof setColorListExternal === 'function') {
          setColorListExternal([baseResolved, ...userColors]);
        }
      } else {
        setBaseColor('');
        setColorList([]);
        if (typeof setColorListExternal === 'function') {
          setColorListExternal([]);
        }
      }
      } else {
        console.error('Update Design failed:', await response.text());
        setErrorState(true);
        setIsProcessing(false);
      }

    } catch (err) {
      console.error('Error during update:', err);
      setErrorState(true);
      setIsProcessing(false);
    }
  };

// ---- Initialize Advanced Color Panel from metadata ----
useEffect(() => {
  if (!hydrateGate) return;
  if (userIntentRef.current === 'upload') return;
  if (baseColor || colorList.length > 0) return;

  const snapColor = (c) => {
    if (!c) return '';
    const found = COLOR_NAMES.find(
      (n) => n.toLowerCase() === String(c).toLowerCase()
    );
    return found || c;
  };

  const raw = [
    (initialBaseColor || '').trim(),
    ...((initialColorList || []).map((c) => (c || '').trim())),
  ].filter(Boolean);

  if (raw.length >= 2 && (processedImage || overlayImage)) {
    const snappedBase = snapColor(raw[0]);
    const snappedRest = raw.slice(1).map(snapColor);

    setBaseColor(snappedBase);
    setColorList(snappedRest);

    const total = Math.min(8, Math.max(2, 1 + snappedRest.length));
    setNumColors(total);
    setPendingNumColors(total);

    setDropdownDefaultsVisible(false);
    setShowColorControls(true);
    setIsProcessed(true);
    setProcessDisabled(false);
  }
}, [
  hydrateGate,
  initAdvancedPanelKey,
  processedImage,
  overlayImage,
  initialBaseColor,
  initialColorList,
  baseColor,
  colorList.length,
  setIsProcessed, // ✅ fixes exhaustive-deps warning
]);


// hydrate base SVG when overlay present but processed missing
useEffect(() => {
  if (!isProcessed) return;
  if (!overlayImage || processedImage) return;
  if (!email || !filename) return;

  const username = (email || '').split('@')[0];
  const baseNoExt = String(filename || '')
    .split('.')
    .slice(0, -1)
    .join('');
  if (!username || !baseNoExt) return;

  const root = `/preview/${username}/output-${baseNoExt}`;
  const ts = Date.now();

  (async () => {
    try {
      const res = await fetch(
        `${normalizeUrl(`${root}/metadata.json`)}?ts=${ts}`,
        { cache: 'no-store' }
      );
      if (!res.ok) {
        console.warn(
          '[ImageProcessor] metadata.json missing in overlay-only hydrate:',
          res.status
        );
        return;
      }

      const meta = await res.json();
      let baseRel = String(meta?.base_svg || '').replace(/^\.\//, '').trim();
      if (!baseRel) return;

      if (!baseRel.includes('/')) baseRel = `vectors/base/${baseRel}`;

      setProcessedLoaded(false);
      setProcessedImage(
        `${normalizeUrl(`${root}/${baseRel}`)}?t=${Date.now()}`
      );
    } catch (err) {
      console.error('[ImageProcessor] overlay-only hydrate failed:', err);
    }
  })();
}, [
  isProcessed,
  overlayImage,
  processedImage,
  email,
  filename,
  normalizeUrl,        // ✅ fixes exhaustive-deps warning
  setProcessedImage,
  setProcessedLoaded,  // ✅ also used in the effect
]);

  // stop spinner when both images ready
  useEffect(() => {
    const bothReady = processedLoaded && (overlayImage ? overlayLoaded : true);
    if (!bothReady) return;

    setIsProcessing(false);

    // Auto-open Advanced Controls after processing finishes (one-shot, stays open)
    if (!autoOpenAdvancedOnceRef.current) {
      autoOpenAdvancedOnceRef.current = true;
      setShowColorControls(true);
      setShowAdvancedOverlayPanel(true);
    }
  }, [processedLoaded, overlayLoaded, overlayImage]);


  // ---- Helper: Advanced Color Controls ----
  const renderAdvancedControls = () => {
    if (!(processedImage && !dropdownDefaultsVisible && showColorControls)) {
      return null;
    }

    const activeCount = numColors || pendingNumColors;
    const indices = [...Array(activeCount).keys()];

    return (
      <>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '1vh',
            fontSize: '1.5vh',
            padding: '0 0.1vw',
            boxSizing: 'border-box',
          }}
        >
        {/* Column headers */}
        <div
          style={{
            display: 'flex',
            width: '100%',
            marginBottom: '0.4vh',
            fontWeight: 'bold',
            textAlign: 'center',
            alignItems: 'center',
          }}
        >
          <div style={{ flexBasis: '40%', maxWidth: '40%' }}>Color</div>
          <div style={{ flexBasis: '18%', maxWidth: '18%' }}>Inflate</div>
          <div style={{ flexBasis: '20%', maxWidth: '20%' }}>Speckle Remove</div>
          <div style={{ flexBasis: '22%', maxWidth: '22%' }}>Threshold</div>
        </div>


          {/* Per-row controls */}
          {indices.map((i) => (
            <div
              key={`adv-row-${i}`}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                marginBottom: '0.2vh',
              }}
            >
              {/* COLOR column */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4vh',
                  flexBasis: '40%',
                  maxWidth: '40%',
                  paddingRight: '0.4vh',
                  boxSizing: 'border-box',
                }}
              >
                {/* [x] button */}
                <button
                  disabled={activeCount <= 2 || i === 0}
                  onClick={async () => {
                    if (activeCount <= 2 || i === 0) return;
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this color?'
                    );
                    if (!confirmed) return;

                    const username = (email || '').split('@')[0];
                    const colorName = i === 0 ? baseColor : colorList[i - 1];
                    const pathSafe = (colorName || '')
                      .replace(/[^a-zA-Z0-9\s-]/g, '')
                      .trim();

                    try {
                      const res = await fetch('/delete-png/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          username,
                          filename,
                          colorIndex: i,
                          colorName: pathSafe,
                        }),
                      });
                      if (!res.ok) {
                        console.error('Delete PNG failed:', await res.text());
                        return;
                      }

                      const newColors = [...colorList];
                      const newInflate = [...inflationValues];
                      const newThresholds = [...thresholdValues];
                      const newSpeckles = [...speckleFlags];

                      if (i > 0) {
                        newColors.splice(i - 1, 1);
                      }
                      newInflate.splice(i, 1);
                      newThresholds.splice(i, 1);
                      newSpeckles.splice(i, 1);

                      const prevCount = activeCount;
                      const newNum = Math.max(2, prevCount - 1);

                      setColorList(newColors);
                      setInflationValues(newInflate);
                      setThresholdValues(newThresholds);
                      setSpeckleFlags(newSpeckles);
                      setPendingNumColors(newNum);
                      setNumColors(newNum);
                      setProcessDisabled(false);

                      const nextInflatePadded = [
                        backgroundInflate,
                        ...Array.from(
                          { length: Math.max(0, newNum - 1) },
                          (_, idx) => Number(newInflate[idx + 1] ?? 0)
                        ),
                      ];
                      const nextSpecklesPadded = Array.from(
                        { length: newNum },
                        (_, idx) => Number(newSpeckles[idx] ?? 0)
                      );
                      const nextThresholdsPadded = Array.from(
                        { length: newNum },
                        (_, idx) => Number(newThresholds[idx] ?? 0)
                      );

                      await handleUpdateDesign({
                        num: newNum,
                        base: baseColor,
                        colors: newColors,
                        background: backgroundInflate,
                        inflate: nextInflatePadded,
                        speckles: nextSpecklesPadded,
                        thresholds: nextThresholdsPadded,
                        rnb: removeNoisyBackground,
                        cbh: closeBackgroundHoles,
                        rsi: removeSmallIslands,
                        sac: speckleAutoCleanup,
                        mfs: makeFreestanding,
                      });
                    } catch (err) {
                      console.error('Error deleting PNG:', err);
                    }
                  }}
                  style={{
                    color: 'red',
                    fontWeight: 'bold',
                    fontSize: '1.5vh',
                    border: 'none',
                    background: 'transparent',
                    cursor:
                      activeCount <= 2 || i === 0 ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>

                {/* Color dropdown */}
                  <select
                    className="adv-select"
                    style={{
                      padding: '0.4vh 0.8vh',
                      fontSize: '1.5vh',
                      flex: 1,
                      minWidth: 0,
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: '#e5e7eb',
                      backdropFilter: 'blur(4px)',
                    }}
                    value={(() => {
                      const current =
                        (i === 0 ? baseColor : colorList[i - 1]) || '';
                      const match = COLOR_NAMES.find(
                        (n) => n.toLowerCase() === current.toLowerCase()
                      );
                      return match || current;
                    })()}
                    onChange={(e) => {
                      const newVal = e.target.value;

                      if (i === 0) {
                        if (newVal === baseColor) return;
                        const nextBase = newVal;
                        setBaseColor(nextBase);

                        const nextSelected = [
                          (nextBase || '').trim(),
                          ...Array.from(
                            { length: activeCount - 1 },
                            (_, idx) => (colorList[idx] || '').trim()
                          ),
                        ].slice(0, activeCount);
                        scheduleColorUpdate(nextSelected);
                      } else {
                        if (newVal === colorList[i - 1]) return;
                        const updatedColors = [...colorList];
                        updatedColors[i - 1] = newVal;
                        setColorList(updatedColors);

                        const nextSelected = [
                          (baseColor || '').trim(),
                          ...Array.from(
                            { length: activeCount - 1 },
                            (_, idx) => (updatedColors[idx] || '').trim()
                          ),
                        ].slice(0, activeCount);
                        scheduleColorUpdate(nextSelected);
                      }
                    }}
                  >

                  {(() => {
                    const current =
                      (i === 0 ? baseColor : colorList[i - 1]) || '';
                    const match = COLOR_NAMES.find(
                      (n) => n.toLowerCase() === current.toLowerCase()
                    );
                    if (!dropdownDefaultsVisible && current && !match) {
                      return <option value={current}>{current}</option>;
                    }
                    return null;
                  })()}

                  {dropdownDefaultsVisible && (
                    <option value="">
                      {i === 0 ? 'Base' : `Color ${i + 1}`}
                    </option>
                  )}

                  {COLOR_NAMES.map((name, idx) => {
                    const isTakenElsewhere =
                      i > 0 &&
                      colorList.some(
                        (val, j) =>
                          j !== i - 1 &&
                          String(val || '')
                            .toLowerCase()
                            .trim() === name.toLowerCase()
                      );
                    return (
                      <option
                        key={idx}
                        value={name}
                        disabled={isTakenElsewhere}
                      >
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* INFLATE column */}
              <div
                style={{
                  flexBasis: '18%',
                  maxWidth: '18%',
                  padding: '0 0.1vh',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25vh',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* – button */}
                  <button
                    type="button"
                    onClick={() => {
                      let val =
                        i === 0
                          ? backgroundInflate
                          : inflationValues[i] || 0;
                      val = val - 1;

                      if (i === 0) {
                        val = Math.max(10, val);
                        setBackgroundInflate(val);
                      } else {
                        val = Math.max(0, val);
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    –
                  </button>

                  {/* numeric input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={
                      i === 0
                        ? backgroundInflate
                        : inflationValues[i] || 0
                    }
                    onChange={(e) => {
                      let raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') raw = i === 0 ? '10' : '0';

                      let val = parseInt(raw, 10);
                      if (!Number.isFinite(val)) val = 0;

                      if (i === 0) {
                        val = Math.max(10, Math.min(100, val));
                        setBackgroundInflate(val);
                      } else {
                        val = Math.max(0, Math.min(10, val));
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '4vh',
                      fontSize: '1.5vh',
                      padding: '0.25vh 0.4vh',
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: '#e5e7eb',
                      textAlign: 'center',
                      outline: 'none',
                      backdropFilter: 'blur(4px)',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* + button */}
                  <button
                    type="button"
                    onClick={() => {
                      let val =
                        i === 0
                          ? backgroundInflate
                          : inflationValues[i] || 0;
                      val = val + 1;

                      if (i === 0) {
                        val = Math.min(100, val);
                        setBackgroundInflate(val);
                      } else {
                        val = Math.min(10, val);
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* SPECKLE column */}
              <div
                style={{
                  flexBasis: '20%',
                  maxWidth: '20%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: i === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={i === 0}
                      checked={!!speckleFlags[i]}
                      onChange={(e) => {
                        if (i === 0) return;
                        const updated = [...speckleFlags];
                        updated[i] = e.target.checked ? 1 : 0;
                        setSpeckleFlags(updated);
                      }}
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        width: '2.2vh',
                        height: '2.2vh',
                        borderRadius: '0.5vh',
                        border: '0.25vh solid rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(15,23,42,0.6)',
                        display: 'inline-block',
                        cursor: i === 0 ? 'not-allowed' : 'pointer',
                      }}
                    />
                    {!!speckleFlags[i] && (
                      <span
                        style={{
                          position: 'absolute',
                          fontSize: '1.7vh',
                          color: 'white',
                          pointerEvents: 'none',
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </span>
                </label>
              </div>

              {/* THRESHOLD column */}
              <div
                style={{
                  flexBasis: '22%',
                  maxWidth: '22%',
                  padding: '0 0.1vh',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25vh',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* – button */}
                  <button
                    type="button"
                    disabled={!speckleFlags[i]}
                    onClick={() => {
                      if (!speckleFlags[i]) return;

                      let val = thresholdValues[i] || 0;
                      val = Math.max(0, val - 1);

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.18)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: speckleFlags[i] ? 'white' : '#9ca3af',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: speckleFlags[i] ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}
                  >
                    –
                  </button>

                  {/* text input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={!speckleFlags[i]}
                    value={thresholdValues[i] || 0}
                    onChange={(e) => {
                      if (!speckleFlags[i]) return;

                      let raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') raw = '0';

                      let val = parseInt(raw, 10);
                      if (!Number.isFinite(val)) val = 0;

                      val = Math.max(0, Math.min(200, val));

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '3.2vh',
                      padding: '0.25vh 0.3vh',
                      fontSize: '1.5vh',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: speckleFlags[i] ? '#e5e7eb' : '#9ca3af',
                      textAlign: 'center',
                      outline: 'none',
                      backdropFilter: 'blur(4px)',
                      cursor: speckleFlags[i] ? 'text' : 'not-allowed',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* + button */}
                  <button
                    type="button"
                    disabled={!speckleFlags[i]}
                    onClick={() => {
                      if (!speckleFlags[i]) return;

                      let val = thresholdValues[i] || 0;
                      val = Math.min(200, val + 1);

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.18)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: speckleFlags[i] ? 'white' : '#9ca3af',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: speckleFlags[i] ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Centered Update button */}
        <div
          style={{
            marginTop: '0.8vh',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={() => handleUpdateDesign()}
            style={{
              padding: '0.4vh 2.2vh',
              fontSize: '1.9vh',
              borderRadius: '8px',
              border: '1px solid #94a3b8',
              fontWeight: 'bold',
              backgroundColor: '#e2e8f0',
              cursor: 'pointer',
            }}
          >
            Update Design
          </button>
        </div>

      </>
    );
  };

// ─────────── RENDER ───────────
// ✅ Disable ALL controls while rendering.gif is visible (isProcessing === true)
const uiDisabled = Boolean(isProcessing);

return (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center', // center horizontally
      justifyContent: 'flex-start', // top-align vertically
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      paddingTop: '0vh',
    }}
  >
    <input
      type="file"
      accept="image/jpeg,image/png"
      ref={fileInputRef}
      onChange={uiDisabled ? undefined : handleFileUpload}
      disabled={uiDisabled}
      style={{ display: 'none' }}
    />

    {/* FLEX ROW: Orange (images) */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '0vw',
        height: 'auto',
      }}
    >
      {/* ORANGE BOX */}
      <div
        ref={orangeBoxRef}
        style={{
          border: '0px solid orange',
          padding: '0.5vw',
          width: '81vmin',
          height: '81vmin',
          maxWidth: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
          display: 'block',
          margin: '0 auto',
        }}
      >
      <InputImageBox
        autoOpenSignal={uploadSessionToken}
        capitanApi={capitanApi}
        inputImage={inputImage}
        uiDisabled={uiDisabled}
        credits={credits}
        onInputClick={handleInputClick}
        isProcessing={isProcessing}
        boxStyle={boxStyle}
        imgStyle={imgStyle}
        invalidateProcessing={invalidateBasicControls}
        controlsConfig={{
          numColors: { enabled: true, min: 2, max: 8 },
          numPicks: { enabled: false },
          shape: { enabled: false },
          flags: {
            removeNoisyBackground: { enabled: true, label: 'Remove Noisy Background' },
            closeBackgroundHoles: { enabled: true, label: 'Close Background Holes' },
            removeSmallIslands: { enabled: true, label: 'Remove Small Islands' },
            speckleAutoCleanup: { enabled: true, label: 'Speckle Auto-Cleanup' },
            makeFreestanding: { enabled: true, label: 'Make Freestanding' },
          },
        }}
        values={{
          pendingNumColors,
          removeNoisyBackground,
          closeBackgroundHoles,
          removeSmallIslands,
          speckleAutoCleanup,
          makeFreestanding,
        }}
        setters={{
          setPendingNumColors,
          setRemoveNoisyBackground,
          setCloseBackgroundHoles,
          setRemoveSmallIslands,
          setSpeckleAutoCleanup,
          setMakeFreestanding,
        }}
      />


<ProcessedImageBox
  resetSessionSignal={uploadSessionToken}
  visible={Boolean(processedImage || overlayImage)}
  processedImage={processedImage}
  overlayImage={overlayImage}

  userEmail={email}
  filename={filename}
  projectRoot={projectRoot}

  onProcessedLoad={() => {
    setProcessedLoaded(true);
    setTimeout(() => {
      orangeBoxRef.current?.scrollTo({
        top: orangeBoxRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  }}
  onOverlayLoad={() => {
    setOverlayLoaded(true);
    setTimeout(() => {
      orangeBoxRef.current?.scrollTo({
        top: orangeBoxRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  }}
  boxStyle={boxStyle}
  layerStyle={layerStyle}
  isProcessing={isProcessing}
  uiDisabled={uiDisabled}
  showAdvancedOverlayPanel={showAdvancedOverlayPanel}
  setShowAdvancedOverlayPanel={setShowAdvancedOverlayPanel}
  showColorControls={showColorControls}
  setShowColorControls={setShowColorControls}
  renderAdvancedControls={renderAdvancedControls}
  capitanApi={capitanApi}
  showAIButton={Boolean(aiModeEnabled)}
  aiButtonDisabled={Boolean(!aiAllowed)}
  aiButtonTitle={
    aiAllowed
      ? 'AI Color Cleanup'
      : 'AI Color Cleanup is available for Patreon/All-Access/Admin tiers'
  }
  onAIConfirm={async () => {
    // optional success-only logic
  }}
  onAIDone={handleAICleanupDone}
/>


      </div>

    </div>

    {/* Unified Process / Progress button */}
    <div
      style={{
        marginTop: '0vh',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <button
        onClick={
          !uiDisabled && credits > 0 && !processDisabled && filename
            ? () => {
                capitanApi?.onProcessStart?.();
                handleProcessClick(false);
              }
            : null
        }
        disabled={
          uiDisabled || // ✅ disable while rendering.gif is displayed
          credits === 0 ||
          processDisabled ||
          !filename // ⬅ require filename (upload finished)
        }
        style={{
          width: '80vmin',
          padding: '0.8vmin 0',
          background: errorState ? '#fecaca' : '#93c5fd',
          border: '0px solid #94a3b8',
          borderRadius: '10px',
          fontSize: '2.8vh',
          fontWeight: 'bold',
          textAlign: 'center',
          cursor:
            !uiDisabled && credits > 0 && !processDisabled && filename
              ? 'pointer'
              : 'not-allowed',
          position: 'relative',
          overflow: 'hidden',
          opacity: uiDisabled ? 0.75 : 1,
        }}
      >
        {isProcessing && progress !== null && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${progress}%`,
              backgroundColor: errorState ? '#f87171' : '#0ea5e9',
              transition: 'width 0.4s ease',
              zIndex: 0,
            }}
          ></div>
        )}

        <span style={{ position: 'relative', zIndex: 2 }}>
          {errorState
            ? 'ERROR'
            : isProcessing
            ? `Processing… ${progress ?? 0}%`
            : 'Process Image'}
        </span>
      </button>
    </div>
  </div>
);
 }

const boxStyle = () => ({
  width: '100%',
  height: '100%',
  border: '2px dashed #94a3b8',
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
});

const imgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const layerStyle = (z) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  top: 0,
  left: 0,
  zIndex: z,
  pointerEvents: 'none',
});